import axios from 'axios';
import * as Sentry from '@sentry/react';
import { isAndroid, openIndexedDB } from './index';

const errorResponse = (err) => {
  // Enhanced error logging for network issues
  const errorInfo = {
    message: err.message,
    code: err.code,
    status: err.response?.status,
    statusText: err.response?.statusText,
    data: err.response?.data,
    headers: err.response?.headers,
    config: {
      url: err.config?.url,
      method: err.config?.method,
      timeout: err.config?.timeout,
      headers: err.config?.headers,
    },
    // Network-specific error details
    isNetworkError: !err.response && !err.request,
    isTimeout: err.code === 'ECONNABORTED',
    isConnectionRefused: err.code === 'ECONNREFUSED',
    isDNSFailure: err.code === 'ENOTFOUND',
    isNetworkUnavailable: err.code === 'ERR_NETWORK',
    requestInfo: err.request
      ? {
          readyState: err.request.readyState,
          status: err.request.status,
          responseURL: err.request.responseURL,
        }
      : null,
  };

  // Log network errors with enhanced details
  if (
    errorInfo.isNetworkError ||
    errorInfo.isTimeout ||
    errorInfo.isConnectionRefused ||
    errorInfo.isDNSFailure ||
    errorInfo.isNetworkUnavailable
  ) {
    Sentry.captureMessage('Network error detected', {
      level: 'error',
      extra: errorInfo,
      tags: {
        errorType: 'network',
        errorCode: err.code || 'unknown',
      },
    });
  }

  if (err.response?.data) {
    return Promise.reject(err.response.data);
  } else {
    return Promise.reject(err);
  }
};
// Admin client for server-side API calls
// Note: baseURL is not used since lab.js passes complete URLs
export const adminClient = axios.create({
  // baseURL intentionally omitted - lab.js constructs full URLs
});

const sosClient = axios.create({
  baseURL: '/api',
});

// Add request interceptor to log outgoing requests
sosClient.interceptors.request.use(
  (config) => {
    Sentry.addBreadcrumb({
      category: 'http',
      message: `Outgoing ${config.method?.toUpperCase()} request`,
      level: 'info',
      data: {
        url: config.url,
        method: config.method,
        baseURL: config.baseURL,
        timeout: config.timeout,
        hasData: !!config.data,
        dataSize: config.data ? JSON.stringify(config.data).length : 0,
      },
    });
    return config;
  },
  (error) => {
    Sentry.captureException(error, {
      tags: { function: 'requestInterceptor' },
    });
    return Promise.reject(error);
  }
);

// Add response interceptor to log successful responses
sosClient.interceptors.response.use((response) => {
  Sentry.addBreadcrumb({
    category: 'http',
    message: `Received ${response.status} response`,
    level: 'info',
    data: {
      url: response.config.url,
      method: response.config.method,
      status: response.status,
      statusText: response.statusText,
      responseSize: response.data ? JSON.stringify(response.data).length : 0,
    },
  });
  return response.data;
}, errorResponse);

adminClient.interceptors.response.use((resp) => resp.data, errorResponse);

export const uploadImage = async (
  retry,
  file,
  fileKey,
  folderName,
  onUploadProgress
) => {
  const maxRetries = 3;

  let retryCount = 0;
  let key = null;
  let url = null;
  let hasAttemptedDelay = false; // Track if we've already tried the delay

  // Capture network information if available
  const networkInfo = {
    online: navigator.onLine,
    connectionType: navigator.connection?.effectiveType || 'unknown',
    downlink: navigator.connection?.downlink || 'unknown',
    rtt: navigator.connection?.rtt || 'unknown',
    saveData: navigator.connection?.saveData || false,
    userAgent: navigator.userAgent,
    platform: navigator.platform,
  };

  Sentry.addBreadcrumb({
    category: 'upload',
    message: 'Uploading file',
    level: 'warning',
    data: {
      name: fileKey,
      fileSize: file.size,
      fileType: file.type,
      folder: folderName,
      networkInfo,
    },
  });

  while (retryCount < maxRetries) {
    try {
      const response = await sosClient.post('cart/signed-url', {
        type: file.type,
        name: fileKey,
        folder: folderName,
      });
      key = response.key;
      url = response.url;

      if (!url || !key) {
        throw new Error('Invalid response from signed-url endpoint');
      }

      try {
        // Primary method: Direct upload to S3
        const putResponse = await axios.put(url, file, {
          headers: {
            'Content-Type': file.type,
          },
          onUploadProgress,
          timeout: 300000, // 5 minutes timeout
        });

        if (putResponse.status >= 200 && putResponse.status < 300) {
          return { key };
        } else {
          const errorMessage =
            putResponse.status === 403
              ? `Upload failed with 403 Forbidden - possible signed URL expiration or S3 policy issue`
              : `Upload failed with status ${putResponse.status}`;
          throw new Error(errorMessage);
        }
      } catch (putError) {
        // Fallback method, form data sent to /upload-fallback api using sdk
        Sentry.captureMessage('Direct upload failed, attempting fallback', {
          extra: {
            putError: putError.message,
            putErrorStatus: putError.response?.status,
            putErrorData: putError.response?.data,
          },
        });

        const formData = new FormData();
        formData.append('file', file);
        formData.append('key', fileKey);
        formData.append('folder', folderName);

        const fallbackResponse = await sosClient.post(
          'cart/upload-fallback',
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
            onUploadProgress,
          }
        );
        if (fallbackResponse && fallbackResponse.success) {
          Sentry.captureMessage('fallback upload successful');
          return { key: fallbackResponse.key };
        } else {
          throw new Error('Fallback upload failed');
        }
      }
    } catch (error) {
      // Check if this is the first failure and we haven't tried the delay yet
      if (!hasAttemptedDelay && retryCount === 0) {
        console.log(
          '⏳ First upload attempt failed, waiting 2 seconds for file to be ready...'
        );
        hasAttemptedDelay = true;

        Sentry.addBreadcrumb({
          category: 'upload',
          message: 'Adding 2-second delay after first upload failure',
          level: 'info',
          data: {
            error: error.message,
            retryCount,
            fileKey,
          },
        });

        // Wait 2 seconds before retrying
        await new Promise((resolve) => setTimeout(resolve, 2000));

        // Try the same upload method again without incrementing retryCount
        continue;
      }

      // Emergency fallback: Save to server when both S3 methods fail
      try {
        Sentry.captureMessage(
          'Both S3 upload methods failed, attempting emergency server storage',
          {
            extra: {
              originalError: error.message,
              originalErrorStatus: error.response?.status,
              originalErrorData: error.response?.data,
            },
          }
        );

        // Enhanced debugging: Test emergency upload preparation
        Sentry.addBreadcrumb({
          category: 'upload',
          message: 'Preparing emergency upload',
          level: 'info',
          data: {
            retryCount,
            fileSize: file.size,
            fileType: file.type,
          },
        });

        // Check if file reader test failed and create a new file from blob if needed
        let fileToUpload = file;

        // Try to get original image from IndexedDB on Android if original file fails
        if (isAndroid()) {
          try {
            console.log('🔍 Trying to get original image from IndexedDB');
            const db = await openIndexedDB();
            const transaction = db.transaction(['originalImages'], 'readonly');
            const store = transaction.objectStore('originalImages');

            // Search all entries and find by filename
            const request = store.openCursor();

            // Use a Promise to properly wait for the IndexedDB lookup
            const indexedDBData = await new Promise((resolve, reject) => {
              let foundEntry = null;

              request.onsuccess = (event) => {
                const cursor = event.target.result;
                if (cursor) {
                  const entry = cursor.value;
                  // Check if this entry matches our file by filename
                  if (entry.filename === file.name) {
                    foundEntry = entry;
                  }
                  cursor.continue();
                } else {
                  // Cursor is done, resolve with found entry
                  resolve(foundEntry);
                }
              };

              request.onerror = () => reject(request.error);
            });

            if (indexedDBData) {
              Sentry.captureMessage(
                'Using IndexedDB original image for emergency upload',
                {
                  extra: {
                    originalFileSize: file.size,
                    storedImageSize: indexedDBData.originalBlob.size,
                    filename: indexedDBData.filename,
                  },
                }
              );

              console.log('using indexdb file'); // Use the original blob to create a new file
              fileToUpload = new File(
                [indexedDBData.originalBlob],
                indexedDBData.filename,
                {
                  type: indexedDBData.originalBlob.type,
                  lastModified: Date.now(),
                }
              );
            }
          } catch (indexedDBError) {
            console.warn(
              'Failed to access IndexedDB for emergency upload:',
              indexedDBError
            );
          }
        }

        // If we don't have IndexedDB data, try creating a new file from blob
        if (fileToUpload === file) {
          console.log("using original file, didn't find indexdb data");
          try {
            // Create a blob from the original file
            const blob = new Blob([file], { type: file.type });

            // Create a new File object from the blob
            const newFile = new File([blob], file.name, {
              type: file.type,
              lastModified: file.lastModified,
            });

            console.log('✅ Successfully created new file from blob:', {
              originalSize: file.size,
              newFileSize: newFile.size,
              originalType: file.type,
              newFileType: newFile.type,
            });

            fileToUpload = newFile;

            Sentry.addBreadcrumb({
              category: 'upload',
              message: 'Created new file from blob',
              level: 'info',
              data: {
                originalSize: file.size,
                newFileSize: newFile.size,
                originalType: file.type,
                newFileType: newFile.type,
                canReadTestFailed: true,
              },
            });
          } catch (blobError) {
            console.error(
              '❌ Failed to create new file from blob:',
              blobError.message
            );
            Sentry.captureMessage('Failed to create new file from blob', {
              level: 'error',
              extra: {
                blobError: blobError.message,
                originalFileSize: file.size,
                originalFileType: file.type,
              },
            });
            // Continue with original file if blob creation fails
            fileToUpload = file;
          }
        }

        // Try S3 upload with the IndexedDB image first (if we have it)
        if (isAndroid() && fileToUpload !== file) {
          try {
            Sentry.captureMessage('Attempting S3 upload with IndexedDB image', {
              extra: {
                originalFileSize: file.size,
                indexedDBFileSize: fileToUpload.size,
                originalFileType: file.type,
                indexedDBFileType: fileToUpload.type,
              },
            });

            // Get a new signed URL for the IndexedDB file
            const signedUrlResponse = await sosClient.post('cart/signed-url', {
              type: fileToUpload.type,
              name: fileKey,
              folder: folderName,
            });

            const s3Key = signedUrlResponse.key;
            const s3Url = signedUrlResponse.url;

            if (!s3Url || !s3Key) {
              throw new Error(
                'Invalid response from signed-url endpoint for IndexedDB upload'
              );
            }

            // Upload the IndexedDB file to S3
            const s3Response = await axios.put(s3Url, fileToUpload, {
              headers: {
                'Content-Type': fileToUpload.type,
              },
              onUploadProgress,
            });

            if (s3Response.status >= 200 && s3Response.status < 300) {
              Sentry.captureMessage(
                'S3 upload with IndexedDB image successful'
              );
              return { key: s3Key };
            } else {
              throw new Error(
                `S3 upload with IndexedDB image failed with status ${s3Response.status}`
              );
            }
          } catch (s3Error) {
            Sentry.captureMessage(
              'S3 upload with IndexedDB image failed, falling back to emergency server storage',
              {
                extra: {
                  s3Error: s3Error.message,
                  s3ErrorStatus: s3Error.response?.status,
                  s3ErrorData: s3Error.response?.data,
                },
              }
            );
            // Continue to emergency server storage
          }
        }

        // Emergency fallback: Save to server when S3 methods fail
        const formData = new FormData();
        formData.append('file', fileToUpload);
        formData.append('key', fileKey);
        formData.append('folder', folderName);

        const emergencyResponse = await sosClient.post(
          'cart/upload-emergency',
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
            onUploadProgress,
          }
        );

        if (emergencyResponse && emergencyResponse.success) {
          Sentry.captureMessage('Emergency server storage successful');
          return {
            key: emergencyResponse.key,
            fileSizeMB: emergencyResponse.fileSizeMB,
            canAttach: emergencyResponse.canAttach,
            downloadUrl: emergencyResponse.downloadUrl,
            emergency: true,
          };
        } else {
          Sentry.captureMessage('Emergency upload failed');
          throw new Error('Emergency upload failed');
        }
      } catch (emergencyError) {
        // If emergency upload also fails, continue with normal retry logic
        retryCount++;

        // Enhanced logging for files when they fail
        const errorData = {
          originalErrorMessage: error.message,
          emergencyErrorMessage: emergencyError.message,
          emergencyErrorResponse: emergencyError.response?.data,
          emergencyErrorStatus: emergencyError.response?.status,
          emergencyErrorHeaders: emergencyError.response?.headers,
          fileSize: file.size,
          fileType: file.type,
        };

        Sentry.addBreadcrumb({
          category: 'upload',
          message: `Upload attempt ${retryCount} failed - all three methods failed`,
          level: 'error',
          data: errorData,
        });

        Sentry.captureException(emergencyError, {
          tags: {
            function: 'uploadImage',
          },
          extra: {
            retryCount,
            fileKey,
            folderName,
            errorMessage: error.message,
            emergencyErrorMessage: emergencyError.message,
            errorResponse: emergencyError.response?.data,
            networkInfo,
            // Enhanced network error details
            networkErrorDetails: {
              isNetworkError:
                !emergencyError.response && !emergencyError.request,
              isTimeout: emergencyError.code === 'ECONNABORTED',
              isConnectionRefused: emergencyError.code === 'ECONNREFUSED',
              isDNSFailure: emergencyError.code === 'ENOTFOUND',
              isNetworkUnavailable: emergencyError.code === 'ERR_NETWORK',
              errorCode: emergencyError.code,
              errorMessage: emergencyError.message,
              requestInfo: emergencyError.request
                ? {
                    readyState: emergencyError.request.readyState,
                    status: emergencyError.request.status,
                    responseURL: emergencyError.request.responseURL,
                  }
                : null,
              configInfo: emergencyError.config
                ? {
                    url: emergencyError.config.url,
                    method: emergencyError.config.method,
                    timeout: emergencyError.config.timeout,
                    headers: emergencyError.config.headers,
                  }
                : null,
            },
          },
        });

        if (retryCount === maxRetries) {
          // If retry is false, throw an error to stop the upload
          if (!retry) {
            throw new Error(
              `Failed to upload after ${maxRetries} attempts: ${emergencyError.message}`
            );
          }
          // If retry is true, return the key to the caller
          Sentry.captureMessage(
            'Upload failed after retry click, skipping upload, image will be missing'
          );
          return { key };
        }

        // Exponential backoff
        const backoffTime = 100 * Math.pow(2, retryCount);
        await new Promise((resolve) => setTimeout(resolve, backoffTime));
      }
    }
  }
};

export const calculatePrice = (data) => {
  return sosClient.post('cart/price', data);
};

export const createCart = (data) => sosClient.post('cart', data);

export const createOrder = (data) => sosClient.post('orders', data);

export const parseAddress = (text) =>
  axios
    .post(
      'https://qjn81caq90.execute-api.us-west-2.amazonaws.com/prod/parse-address',
      text
    )
    .then((resp) => resp.data);

export const recoverAccountId = (email) =>
  sosClient.post('cart/recover-account', { email });

// Utility function to check if a file object is likely stale
export const isFileStale = (file) => {
  try {
    const fileAge = Date.now() - file.lastModified;
    return fileAge > 300000; // 5 minutes
  } catch (error) {
    console.warn('⚠️ Could not check file staleness:', error.message);
    return false;
  }
};
